/**
 * Abrechnungszeitraum-Funktionen für die Lohnbuchhaltung.
 *
 * Ein "Monat" in der Lohnbuchhaltung läuft vom 21. des Vormonats
 * bis zum 20. des aktuellen Monats.
 *
 * Beispiel: "Februar 2026" = 21.01.2026 – 20.02.2026
 */

export interface PayrollPeriod {
  /** Erster Tag des Abrechnungszeitraums (21. des Vormonats) */
  start: Date;
  /** Letzter Tag des Abrechnungszeitraums (20. des Monats) */
  end: Date;
  /** Anzeige-Label, z.B. "Februar 2026 (21.01.–20.02.)" */
  label: string;
  /** Monat (1-12) */
  month: number;
  /** Jahr */
  year: number;
}

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/**
 * Berechnet den Abrechnungszeitraum für einen Monat.
 * @param year - Jahr des Abrechnungsmonats
 * @param month - Monat (1-12)
 */
export function getPayrollPeriod(year: number, month: number): PayrollPeriod {
  // Start: 21. des Vormonats
  let startYear = year;
  let startMonth = month - 1; // 0-basiert für Date-Konstruktor
  if (startMonth === 0) {
    startMonth = 12;
    startYear = year - 1;
  }
  const start = new Date(startYear, startMonth - 1, 21);

  // Ende: 20. des aktuellen Monats
  const end = new Date(year, month - 1, 20);

  const pad = (n: number) => n.toString().padStart(2, '0');
  const startLabel = `${pad(start.getDate())}.${pad(start.getMonth() + 1)}.`;
  const endLabel = `${pad(end.getDate())}.${pad(end.getMonth() + 1)}.`;

  return {
    start,
    end,
    label: `${MONTH_NAMES[month - 1]} ${year} (${startLabel}–${endLabel})`,
    month,
    year,
  };
}

/**
 * Gibt alle 12 Abrechnungszeiträume für ein Jahr zurück.
 */
export function getPayrollPeriodsForYear(year: number): PayrollPeriod[] {
  return Array.from({ length: 12 }, (_, i) => getPayrollPeriod(year, i + 1));
}

/**
 * Prüft ob ein Datumsstring (YYYY-MM-DD) im Abrechnungszeitraum liegt.
 * Zeitzonen-sicher: Vergleich auf Datums-Strings, nicht auf Date-Objekten.
 */
export function isInPayrollPeriod(dateStr: string, period: PayrollPeriod): boolean {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const startStr = `${period.start.getFullYear()}-${pad(period.start.getMonth() + 1)}-${pad(period.start.getDate())}`;
  const endStr = `${period.end.getFullYear()}-${pad(period.end.getMonth() + 1)}-${pad(period.end.getDate())}`;
  return dateStr >= startStr && dateStr <= endStr;
}

/**
 * Gibt den aktuellen Abrechnungsmonat zurück.
 * Wenn heute <= 20. → aktueller Kalendermonat, sonst nächster.
 */
export function getCurrentPayrollMonth(): { year: number; month: number } {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1; // 1-basiert
  if (now.getDate() > 20) {
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }
  return { year, month };
}

/**
 * Navigiert einen Abrechnungsmonat zurück.
 */
export function prevPayrollMonth(m: { year: number; month: number }): { year: number; month: number } {
  if (m.month === 1) return { year: m.year - 1, month: 12 };
  return { year: m.year, month: m.month - 1 };
}

/**
 * Navigiert einen Abrechnungsmonat vor.
 */
export function nextPayrollMonth(m: { year: number; month: number }): { year: number; month: number } {
  if (m.month === 12) return { year: m.year + 1, month: 1 };
  return { year: m.year, month: m.month + 1 };
}

const MONTH_NAMES_SHORT = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/**
 * Formatiert einen Abrechnungsmonat, z.B. "Februar 2026".
 */
export function formatPayrollMonth(m: { year: number; month: number }): string {
  return `${MONTH_NAMES_SHORT[m.month - 1]} ${m.year}`;
}

/**
 * Formatiert eine Zahl im deutschen Format (Komma als Dezimaltrenner).
 */
export function formatDE(value: number, decimals = 2): string {
  return value.toFixed(decimals).replace('.', ',');
}
